import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Loader2,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { accountApi } from '@/api/commercial/accounts';
import {
  type CreditAnalysis,
  CreditAnalysisStatus,
  creditAnalysisApi,
} from '@/api/commercial/credit-analysis';
import { CreditAnalysisTimeline } from '@/components/commercial/credit-analysis/credit-analysis-timeline';
import { CreditAnalysisVerdictDetails } from '@/components/commercial/credit-analysis/credit-analysis-verdict-details';
import { DocumentUploadForm } from '@/components/forms/commercial/credit-analysis/document-upload-form';
import { ResendDocumentRequestDialog } from '@/components/forms/commercial/credit-analysis/resend-document-request-dialog';
import { SendDocumentRequestForm } from '@/components/forms/commercial/credit-analysis/send-document-request-form';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useUpdateProposal } from '@/context/update-proposal-context';
import { useCreditAnalysisExpiration } from '@/hooks/commercial';
import {
  normalizeAccountTypeForCreditAnalysis,
  useCreditAnalysisStateMachine,
} from '@/hooks/commercial/use-credit-analysis-state-machine';
import {
  useInvalidateProposalCreditAnalysis,
  useProposalCreditAnalysisResolution,
} from '@/hooks/commercial/use-proposal-credit-analysis-resolution';
import { useToast } from '@/hooks/use-toast';
import { useProposalNavigation } from '../shared/use-proposal-navigation';

const CREDIT_ANALYSIS_STATUS = {
  NOT_STARTED: 'NOT_STARTED',
  PENDING_EXTERNAL_DOCS: 'PENDING_EXTERNAL_DOCS',
  PENDING_INTERNAL_ANALYSIS: 'PENDING_INTERNAL_ANALYSIS',
  DOC_DECLINED: 'DOC_DECLINED',
  APPROVED: 'APPROVED',
  PARTIALLY_APPROVED: 'PARTIALLY_APPROVED',
  DECLINED: 'DECLINED',
  EXPIRED: 'EXPIRED',
} as const;

interface StepCreditAnalysisUpdateProps {
  proposalId: string;
}

export function StepCreditAnalysisUpdate({ proposalId }: StepCreditAnalysisUpdateProps) {
  const _navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const invalidateProposalCreditAnalysis = useInvalidateProposalCreditAnalysis();
  const navigation = useProposalNavigation({ mode: 'update', proposalId });
  const {
    data: { account, contacts, proposal, creditAnalysis },
    isLoading,
    refreshCreditAnalysis,
    updateProposal,
  } = useUpdateProposal();

  const [localCreditAnalysis, setLocalCreditAnalysis] = useState<CreditAnalysis | null>(
    creditAnalysis,
  );
  const [resendDialogOpen, setResendDialogOpen] = useState(false);

  const status = localCreditAnalysis?.status ?? CREDIT_ANALYSIS_STATUS.NOT_STARTED;
  const hasCreditAnalysis = !!localCreditAnalysis;
  const accountType = normalizeAccountTypeForCreditAnalysis(
    account?.accountType,
    account?.simeiSince,
    account?.document?.type,
  );

  const expirationValidation = useCreditAnalysisExpiration(localCreditAnalysis, proposal?.status);

  const stateMachine = useCreditAnalysisStateMachine({
    creditAnalysis: localCreditAnalysis,
    accountType,
  });
  const declinedDocuments = stateMachine.declinedDocuments;

  const filteredContacts = contacts.filter((contact) =>
    contact.contactItems?.some((item) => item.type === 'Email'),
  );

  const { data: accountDocuments } = useQuery({
    queryKey: ['accountDocuments', account?.id],
    queryFn: async () => {
      if (!account?.id) return null;
      return accountApi.findDocuments(account.id);
    },
    enabled: !!account?.id,
  });

  const allEmails: string[] = [
    ...(accountDocuments?.subjects?.map((s) => s.value) || []),
    ...(accountDocuments?.externalSubjects?.map((e) => e.value) || []),
  ];

  const { data: creditResolution, isLoading: isCreditResolutionLoading } =
    useProposalCreditAnalysisResolution(proposal?.id);

  const { data: timelineEntries } = useQuery({
    queryKey: ['creditAnalysisTimeline', localCreditAnalysis?.id],
    queryFn: async () => {
      if (!localCreditAnalysis?.id) return [];
      try {
        const data = await creditAnalysisApi.getTimeline(localCreditAnalysis.id);
        return data;
      } catch {
        return [];
      }
    },
    enabled: !!localCreditAnalysis?.id,
  });

  useEffect(() => {
    console.log('[CREDIT ANALYSIS UPDATE DEBUG] === PROPOSAL CHANGE ===');
    console.log('[CREDIT ANALYSIS UPDATE DEBUG] Proposal ID:', proposal?.id);
    console.log(
      '[CREDIT ANALYSIS UPDATE DEBUG] Proposal creditAnalysisId:',
      proposal?.creditAnalysisId,
    );
    const analysis = creditResolution?.analysis ?? null;
    console.log('[CREDIT ANALYSIS UPDATE DEBUG] SELECTED analysis:', {
      id: analysis?.id,
      source: creditResolution?.source,
      resolutionStatus: creditResolution?.status,
      status: analysis?.status,
      proposalId: analysis?.proposalId,
      newAnalysisIn: analysis?.newAnalysisIn,
    });

    if (analysis) {
      console.log('[CREDIT ANALYSIS UPDATE DEBUG] Setting localCreditAnalysis');
      setLocalCreditAnalysis(analysis);
    } else {
      console.log('[CREDIT ANALYSIS UPDATE DEBUG] NO ANALYSIS FOUND');
      setLocalCreditAnalysis(null);
    }
  }, [creditResolution, proposal?.id, proposal?.creditAnalysisId]);

  const handleBack = () => {
    navigation.goToClient();
  };

  const handleNextStep = () => {
    const isApproved =
      status === CREDIT_ANALYSIS_STATUS.APPROVED ||
      status === CREDIT_ANALYSIS_STATUS.PARTIALLY_APPROVED;

    if (isApproved) {
      navigation.goToCart();
    }
  };

  const handleRequestNewAnalysis = async () => {
    if (!account?.id || !proposal?.id) return;

    try {
      const newAnalysis = await creditAnalysisApi.create({
        accountId: account.id,
        proposalId: proposal.id,
        status: CreditAnalysisStatus.PENDING_ANALYSIS,
      });

      await updateProposal({ creditAnalysisId: newAnalysis.id });
      setLocalCreditAnalysis(newAnalysis);
      await refreshCreditAnalysis();
      await invalidateProposalCreditAnalysis(proposal.id);

      await queryClient.invalidateQueries({ queryKey: ['creditAnalysis', 'account', account.id] });
      await queryClient.invalidateQueries({
        queryKey: ['creditAnalysis', 'proposal', proposal.id],
      });
      await queryClient.refetchQueries({ queryKey: ['creditAnalysis', 'proposal', proposal.id] });

      toast({
        variant: 'success',
        title: 'Nova análise criada',
        description: 'Uma nova análise de crédito foi criada com sucesso.',
      });
    } catch (_error) {
      toast({
        title: 'Erro',
        description: 'Erro ao criar nova análise de crédito.',
        variant: 'destructive',
      });
    }
  };

  const handleDocumentRequestSuccess = async (newCreditAnalysis: CreditAnalysis) => {
    setLocalCreditAnalysis(newCreditAnalysis);
    await refreshCreditAnalysis();
    if (proposal?.id) await invalidateProposalCreditAnalysis(proposal.id);
  };

  const handleDocumentUploadSuccess = async (updatedCreditAnalysis: CreditAnalysis) => {
    setLocalCreditAnalysis(updatedCreditAnalysis);
    await refreshCreditAnalysis();
    if (proposal?.id) await invalidateProposalCreditAnalysis(proposal.id);
  };

  if (!proposal?.id) {
    return (
      <div className="space-y-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Proposta não encontrada</AlertTitle>
          <AlertDescription>
            É necessário criar uma proposta antes de prosseguir com a análise de crédito.
          </AlertDescription>
        </Alert>
        <Button variant="outline" onClick={handleBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar para seleção de cliente
        </Button>
      </div>
    );
  }

  if (contacts.length === 0) {
    return (
      <div className="space-y-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Contatos não encontrados</AlertTitle>
          <AlertDescription>
            É necessário selecionar pelo menos um contato antes de prosseguir com a análise de
            crédito.
          </AlertDescription>
        </Alert>
        <Button variant="outline" onClick={handleBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar para seleção de cliente
        </Button>
      </div>
    );
  }

  if (isLoading || isCreditResolutionLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {creditResolution?.isValid &&
        !expirationValidation.isExpired &&
        (status === CREDIT_ANALYSIS_STATUS.APPROVED ||
          status === CREDIT_ANALYSIS_STATUS.PARTIALLY_APPROVED) && (
          <Alert variant="success">
            <CheckCircle className="h-4 w-4" />
            <AlertTitle>Análise em dia</AlertTitle>
            <AlertDescription>
              Não há análise pendente! A análise de crédito está válida.
            </AlertDescription>
          </Alert>
        )}

      {creditResolution && !creditResolution.isValid && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Análise de crédito não liberada</AlertTitle>
          <AlertDescription>{creditResolution.message}</AlertDescription>
        </Alert>
      )}

      {((creditResolution?.isValid &&
        (status === CREDIT_ANALYSIS_STATUS.APPROVED ||
          status === CREDIT_ANALYSIS_STATUS.PARTIALLY_APPROVED)) ||
        status === CREDIT_ANALYSIS_STATUS.DECLINED) &&
        localCreditAnalysis && (
          <CreditAnalysisVerdictDetails
            creditAnalysis={localCreditAnalysis}
            onRequestNewAnalysis={handleRequestNewAnalysis}
          />
        )}

      {status === CREDIT_ANALYSIS_STATUS.DOC_DECLINED && (
        <>
          <Alert variant="destructive" className="mb-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Documentos reprovados</AlertTitle>
            <AlertDescription>
              Alguns documentos foram reprovados. Por favor, envie novos documentos para continuar
              com a análise.
            </AlertDescription>
          </Alert>
          <Card>
            <CardHeader>
              <CardTitle>Reenviar Documentos</CardTitle>
            </CardHeader>
            <CardContent>
              <DocumentUploadForm
                proposalId={proposal.id}
                account={account || null}
                accountType={accountType}
                creditAnalysis={localCreditAnalysis}
                requiredDocuments={stateMachine.requiredDocuments.map((f) => f.key)}
                declinedDocuments={declinedDocuments}
                onSuccess={handleDocumentUploadSuccess}
              />
            </CardContent>
          </Card>
        </>
      )}

      {status === CREDIT_ANALYSIS_STATUS.EXPIRED && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Análise expirada</AlertTitle>
          <AlertDescription>
            Esta análise de crédito expirou. É necessário solicitar uma nova análise.
          </AlertDescription>
          <Button onClick={handleRequestNewAnalysis} className="mt-4">
            Solicitar nova análise
          </Button>
        </Alert>
      )}

      {status === CREDIT_ANALYSIS_STATUS.NOT_STARTED && (
        <SendDocumentRequestForm
          proposalId={proposal.id}
          accountId={account?.id}
          account={account}
          contacts={filteredContacts}
          onSuccess={handleDocumentRequestSuccess}
        />
      )}

      {hasCreditAnalysis && status !== CREDIT_ANALYSIS_STATUS.NOT_STARTED && (
        <>
          <CardTitle>Histórico</CardTitle>
          <CreditAnalysisTimeline
            creditAnalysisId={localCreditAnalysis?.id || null}
            entries={timelineEntries}
            creditAnalysis={localCreditAnalysis}
          />
        </>
      )}

      {status === CREDIT_ANALYSIS_STATUS.PENDING_EXTERNAL_DOCS && (
        <div className="flex justify-end w-full">
          <Button onClick={() => setResendDialogOpen(true)}>Reenviar Solicitação</Button>
        </div>
      )}

      <div className="flex justify-between">
        <Button variant="outline" onClick={handleBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>

        {creditResolution?.isValid &&
          (status === CREDIT_ANALYSIS_STATUS.APPROVED ||
            status === CREDIT_ANALYSIS_STATUS.PARTIALLY_APPROVED) && (
            <Button onClick={handleNextStep}>
              Próxima etapa
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}
      </div>

      <ResendDocumentRequestDialog
        open={resendDialogOpen}
        onClose={() => setResendDialogOpen(false)}
        proposalId={proposal.id}
        accountId={account?.id}
        contacts={contacts}
        recipients={allEmails}
        creditAnalysisId={localCreditAnalysis?.id || ''}
        onSuccess={handleDocumentRequestSuccess}
      />
    </div>
  );
}
